import type { ApsViewable } from "../stores/accModelStore";

/** Personal / collaboration 3D views — not useful for compliance review. */
const PERSONAL_3D = /^\{?\s*3D\s*-\s*[^}]+\}?$/i;

/** Revit documentation working views — not permit sheets. */
const DOCUMENTATION_VIEW = /^Documentation_/i;

function isComplianceSheet(v: ApsViewable): boolean {
  if (v.kind === "2d") {
    if (DOCUMENTATION_VIEW.test(v.name)) return false;
    // Show all translated 2D sheets (A-series, options, elevations, etc.).
    return true;
  }
  if (PERSONAL_3D.test(v.name.trim())) return false;
  if (DOCUMENTATION_VIEW.test(v.name)) return false;
  return (
    v.kind === "3d" &&
    (/new\s*construction/i.test(v.name) || !/3D\s*-/i.test(v.name))
  );
}

function sortForPicker(a: ApsViewable, b: ApsViewable): number {
  const rank = (v: ApsViewable): number => {
    if (v.kind === "3d") return 100;
    if (/^A2[.-].*floor\s*plan/i.test(v.name)) return 0;
    if (/floor\s*plan/i.test(v.name)) return 1;
    if (/^A0/i.test(v.name)) return 2;
    if (/^A1/i.test(v.name)) return 3;
    if (/^A2/i.test(v.name)) return 4;
    return 50;
  };
  const d = rank(a) - rank(b);
  if (d !== 0) return d;
  return a.name.localeCompare(b.name);
}

/**
 * Curate APS viewables for the sheet picker — all 2D sheets plus one useful
 * 3D model (hides personal {3D - user} views and Revit documentation views).
 */
export function filterAccViewablesForPicker(
  viewables: ApsViewable[]
): ApsViewable[] {
  const curated = viewables.filter(isComplianceSheet).sort(sortForPicker);
  if (curated.length > 0) return curated;

  const twoD = viewables.filter(v => v.kind === "2d");
  const threeD = viewables.find(v => v.kind === "3d");
  const fallback = threeD ? [...twoD, threeD] : twoD;
  return fallback.length > 0 ? fallback : viewables;
}

export function pickDefaultAccViewable(viewables: ApsViewable[]): string {
  const floor2d = viewables.find(
    v =>
      v.kind === "2d" &&
      (/^A2\.1\s*-\s*FLOOR\s*PLAN/i.test(v.name) ||
        (/a2/i.test(v.name) && /floor\s*plan/i.test(v.name)))
  );
  if (floor2d) return floor2d.id;
  const any2d = viewables.find(v => v.kind === "2d");
  if (any2d) return any2d.id;
  const any3d = viewables.find(v => v.kind === "3d");
  return any3d?.id ?? viewables[0]?.id ?? "";
}
